import axios from "axios";

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5005/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add token to headers
api.interceptors.request.use(
  (config) => {
    // Skip adding token for login and register endpoints
    const isAuthEndpoint =
      config.url?.includes("/login") || config.url?.includes("/register");
    console.log("isAuthEndpoint", isAuthEndpoint);
    if (!isAuthEndpoint) {
      const token = localStorage.getItem("adminToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Admin Authentication APIs
export const adminAuthAPI = {
  login: (credentials) => api.post("/admin/login", credentials),
  getProfile: () => api.get("/admin/profile"),
};

// Admin User Management APIs
export const adminUserAPI = {
  getAll: (params) => api.get("/admin/users", { params }),
  getById: (id) => api.get(`/admin/users/${id}`),
  create: (userData) => api.post("/admin/users", userData),
  update: (id, userData) => api.put(`/admin/users/${id}`, userData),
  delete: (id) => api.delete(`/admin/users/${id}`),
};

// Admin Dashboard APIs
export const adminDashboardAPI = {
  getStats: () => api.get("/admin/dashboard/stats"),
};

// Admin Brand Management APIs
export const adminBrandAPI = {
  getAll: (params) => api.get("/admin/brands", { params }),
  getById: (id) => api.get(`/admin/brands/${id}`),
  create: (brandData) => api.post("/admin/brands", brandData),
  update: (id, brandData) => api.put(`/admin/brands/${id}`, brandData),
  delete: (id) => api.delete(`/admin/brands/${id}`),
};

// Admin Product Category Management APIs
export const adminCategoryAPI = {
  getAll: (params) => api.get("/admin/categories", { params }),
  getById: (id) => api.get(`/admin/categories/${id}`),
  create: (categoryData) => api.post("/admin/categories", categoryData),
  update: (id, categoryData) =>
    api.put(`/admin/categories/${id}`, categoryData),
  delete: (id) => api.delete(`/admin/categories/${id}`),
};

// Admin Product Management APIs
export const adminProductAPI = {
  getAll: (params) => api.get("/admin/products", { params }),
  getById: (id) => api.get(`/admin/products/${id}`),
  create: (productData) => api.post("/admin/products", productData),
  update: (id, productData) => api.put(`/admin/products/${id}`, productData),
  delete: (id) => api.delete(`/admin/products/${id}`),
  activate: (id) => api.patch(`/admin/products/${id}/activate`),
  deactivate: (id) => api.patch(`/admin/products/${id}/deactivate`),
  getStats: () => api.get("/admin/products/stats"),
  bulkDelete: (productIds) =>
    api.post("/admin/products/bulk-delete", { productIds }),
  bulkUpdateStatus: (productIds, status) =>
    api.post("/admin/products/bulk-status", { productIds, status }),
  clearAll: () => api.delete("/admin/products/clear-all"),
  uploadExcel: (formData) =>
    api.post("/admin/products/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getFilterOptions: () => api.get("/admin/products/filter-options"),
};

// Admin Promotion Management APIs
export const adminPromotionAPI = {
  getAll: (params) => api.get("/admin/promotions", { params }),
  getById: (id) => api.get(`/admin/promotions/${id}`),
  create: (promotionData) => api.post("/admin/promotions", promotionData),
  update: (id, promotionData) =>
    api.put(`/admin/promotions/${id}`, promotionData),
  delete: (id) => api.delete(`/admin/promotions/${id}`),
  activate: (id) => api.patch(`/admin/promotions/${id}/activate`),
  deactivate: (id) => api.patch(`/admin/promotions/${id}/deactivate`),
  getActive: () => api.get("/admin/promotions/active"),
};

// Admin Blog Management APIs
export const adminBlogAPI = {
  getAll: (params) => api.get("/admin/blogs", { params }),
  getById: (id) => api.get(`/admin/blogs/${id}`),
  create: (blogData) => api.post("/admin/blogs", blogData),
  update: (id, blogData) => api.put(`/admin/blogs/${id}`, blogData),
  delete: (id) => api.delete(`/admin/blogs/${id}`),
  publish: (id) => api.patch(`/admin/blogs/${id}/publish`),
  unpublish: (id) => api.patch(`/admin/blogs/${id}/unpublish`),
  getPublished: (params) => api.get("/admin/blogs/published", { params }),
};

// Admin SEO Management APIs
export const adminSeoAPI = {
  getAll: () => api.get('/admin/seo'),
  getByPageKey: (pageKey) => api.get(`/admin/seo/${pageKey}`),
  upsert: (pageKey, seoData) => api.put(`/admin/seo/${pageKey}`, seoData),
};

export default api;
